/**
 * Pro unlock — multi-platform purchase flow.
 * Ported from Lattice's entitlements.ts with Doublet SKU + price.
 *
 * Three payment rails, picked at runtime via feature detection:
 *   1. iOS Capacitor → RevenueCat (StoreKit 2 under the hood)
 *   2. Android TWA   → Digital Goods API (Play Billing)
 *   3. Plain web     → 'unsupported' (mock unlock retained for dev only)
 *
 * Each rail flips the same localStorage flag via storage.ts:setUnlocked(),
 * so the rest of the React app doesn't care which rail sold the unlock.
 * RevenueCat ties ownership to the Apple ID; DGAPI ties to the Play
 * account — both restore automatically on a fresh device.
 *
 * Pricing: $0.99 USD (Doublet — see doublet_locked_decisions.md).
 */
import { isUnlocked, setUnlocked } from './storage';

const SKU = 'doublet_pro_unlock';
const RC_ENTITLEMENT = 'pro_unlock';

export type PurchaseOutcome = 'success' | 'cancelled' | 'failed' | 'unsupported';

export interface PurchaseResult {
  outcome: PurchaseOutcome;
  error?: string;
}

interface DigitalGoodsService {
  getDetails(itemIds: string[]): Promise<ItemDetails[]>;
  listPurchases(): Promise<PurchaseDetails[]>;
}
interface ItemDetails {
  itemId: string;
  title?: string;
  description?: string;
  price?: { currency: string; value: string };
}
interface PurchaseDetails {
  itemId: string;
  purchaseToken: string;
}

// Build-time testing mode. Set via VITE_TESTING_MODE=true in .env.testing
// (picked up by `vite build --mode testing`). When on:
//   - hasFullUnlockLocal() returns true regardless of stored state
//   - all 10 worlds are accessible without paying
// Off (default `vite build`): paywall behaves normally.
export function isTestingMode(): boolean {
  return import.meta.env.VITE_TESTING_MODE === 'true';
}

// Runtime "clean screenshots" mode. Set via ?screenshots=1 URL param.
// Forces the paywall to render (overriding testing mode) so you can
// capture App Store / Play Console listing images without testing
// banners or auto-unlocked worlds. Doesn't change gameplay otherwise.
export function isScreenshotMode(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('screenshots') === '1';
  } catch {
    return false;
  }
}

export function hasFullUnlockLocal(): boolean {
  if (isScreenshotMode()) return false;
  if (isTestingMode()) return true;
  return isUnlocked();
}

export function clearEntitlementLocal(): void {
  setUnlocked(false);
}

let _platform: 'ios-capacitor' | 'web' | null = null;
export async function detectPlatform(): Promise<'ios-capacitor' | 'web'> {
  if (_platform !== null) return _platform;
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      _platform = 'ios-capacitor';
      return _platform;
    }
  } catch {
    // @capacitor/core absent at runtime — fine, treat as web.
  }
  _platform = 'web';
  return _platform;
}

let _rcInitialized = false;
async function ensureRevenueCatConfigured(): Promise<boolean> {
  if (_rcInitialized) return true;
  const apiKey = (import.meta as unknown as { env: Record<string, string | undefined> }).env
    ?.VITE_REVENUECAT_IOS_API_KEY;
  if (!apiKey) {
    console.warn('[entitlements] VITE_REVENUECAT_IOS_API_KEY not set — iOS IAP disabled');
    return false;
  }
  try {
    const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');
    await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
    await Purchases.configure({ apiKey });
    _rcInitialized = true;
    return true;
  } catch (e) {
    console.warn('[entitlements] RevenueCat configure failed', e);
    return false;
  }
}

// ---------- iOS (RevenueCat) rail ----------

async function iosQueryOwnership(): Promise<boolean> {
  if (!(await ensureRevenueCatConfigured())) return false;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const info = await Purchases.getCustomerInfo();
    const active = info.customerInfo.entitlements.active;
    return Object.prototype.hasOwnProperty.call(active, RC_ENTITLEMENT);
  } catch (e) {
    console.warn('[entitlements] iOS getCustomerInfo failed', e);
    return false;
  }
}

async function iosPurchase(): Promise<PurchaseResult> {
  if (!(await ensureRevenueCatConfigured())) {
    return { outcome: 'unsupported', error: 'RevenueCat not configured' };
  }
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const offerings = await Purchases.getOfferings();
    const currentOffering = offerings.current;
    if (!currentOffering) {
      return { outcome: 'failed', error: 'No RevenueCat current offering configured' };
    }
    const pkg = currentOffering.availablePackages[0];
    if (!pkg) {
      return { outcome: 'failed', error: 'Offering has no available packages' };
    }
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    const owned = Object.prototype.hasOwnProperty.call(
      customerInfo.entitlements.active,
      RC_ENTITLEMENT,
    );
    if (owned) {
      setUnlocked(true);
      return { outcome: 'success' };
    }
    return { outcome: 'failed', error: 'Purchase completed but entitlement not granted' };
  } catch (e) {
    const err = e as { code?: string | number; message?: string };
    if (err?.code === 'PURCHASE_CANCELLED_ERROR' || err?.code === 1) {
      return { outcome: 'cancelled' };
    }
    const msg = err?.message ?? String(e);
    console.warn('[entitlements] iOS purchase failed', e);
    return { outcome: 'failed', error: `iOS: ${msg}` };
  }
}

// ---------- Android TWA (Digital Goods API) rail ----------

function getService(): Promise<DigitalGoodsService | null> {
  try {
    const getter = (window as unknown as {
      getDigitalGoodsService?: (id: string) => Promise<DigitalGoodsService | null>;
    }).getDigitalGoodsService;
    if (typeof getter !== 'function') return Promise.resolve(null);
    return getter('https://play.google.com/billing').catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

export async function isDigitalGoodsAvailable(): Promise<boolean> {
  const svc = await getService();
  return svc !== null;
}

export async function isPurchaseAvailable(): Promise<boolean> {
  const platform = await detectPlatform();
  if (platform === 'ios-capacitor') return ensureRevenueCatConfigured();
  return isDigitalGoodsAvailable();
}

// Re-query the platform's purchase rail. Call on app boot to catch
// cross-device unlocks; the local flag updates if ownership turned on.
export async function refreshEntitlement(): Promise<boolean> {
  const local = hasFullUnlockLocal();
  const platform = await detectPlatform();
  if (platform === 'ios-capacitor') {
    const owned = await iosQueryOwnership();
    if (owned && !local) setUnlocked(true);
    return owned || local;
  }
  // Android TWA / web — DGAPI path
  const svc = await getService();
  if (!svc) return local;
  try {
    const purchases = await svc.listPurchases();
    const owned = purchases.some((p) => p.itemId === SKU);
    if (owned && !local) setUnlocked(true);
    return owned || local;
  } catch (e) {
    console.warn('[entitlements] listPurchases failed', e);
    return local;
  }
}

export async function purchaseFullUnlock(): Promise<PurchaseResult> {
  const platform = await detectPlatform();
  if (platform === 'ios-capacitor') return iosPurchase();

  const svc = await getService();
  if (!svc) return { outcome: 'unsupported', error: 'getDigitalGoodsService returned null' };
  if (typeof PaymentRequest === 'undefined') {
    return { outcome: 'unsupported', error: 'PaymentRequest not defined' };
  }

  let stage = 'init';
  try {
    stage = 'listPurchases-pre';
    const existing = await svc.listPurchases();
    if (existing.some((p) => p.itemId === SKU)) {
      setUnlocked(true);
      return { outcome: 'success' };
    }

    stage = 'paymentRequest-new';
    const request = new PaymentRequest(
      [{
        supportedMethods: 'https://play.google.com/billing',
        data: { sku: SKU },
      }],
      {
        total: {
          label: 'Doublet — unlock all 10 worlds',
          amount: { currency: 'USD', value: '0.99' },
        },
      },
    );

    stage = 'paymentRequest-show';
    const response = await request.show();

    stage = 'paymentRequest-complete';
    await response.complete('success');

    stage = 'listPurchases-post';
    for (let attempt = 0; attempt < 2; attempt++) {
      const latest = await svc.listPurchases();
      if (latest.some((p) => p.itemId === SKU)) {
        setUnlocked(true);
        return { outcome: 'success' };
      }
      if (attempt === 0) await new Promise((r) => setTimeout(r, 400));
    }
    setUnlocked(true);
    return { outcome: 'success' };
  } catch (e) {
    const err = e as { name?: string; message?: string };
    if (err?.name === 'AbortError') {
      return { outcome: 'cancelled', error: `stage=${stage}` };
    }
    const name = err?.name ?? 'Error';
    const msg = err?.message ?? String(e);
    console.warn('[entitlements] purchase failed', stage, e);
    return { outcome: 'failed', error: `${name} @ ${stage}: ${msg}` };
  }
}

export function initEntitlement(onChange: (fullUnlock: boolean) => void): boolean {
  const initial = hasFullUnlockLocal();
  void refreshEntitlement().then((now) => {
    if (now !== initial) onChange(now);
  });
  return initial;
}
